import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import BlogSidebar from "../components/blog/BlogSidebar";
import { COVER_ASPECT, getArticle, thumbOf } from "../lib/blogApi";
import type { BlogArticle as Article } from "../lib/blogApi";
import { assetUrl } from "../lib/api";
import { longDate } from "../lib/format";
import { renderMarkdown } from "../lib/markdown";
import { ORGANIZATION_ID, SITE_NAME, absoluteUrl, breadcrumbLd, faqLd, useSeo } from "../lib/seo";
import { useLang, useT } from "../i18n";


/** /blog/:slug — one article (Markdown, rendered safely) with the blog sidebar. */
export default function BlogArticle() {
  const t = useT();
  const b = t.blog;
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const lang = useLang();
  const key = `${slug}|${lang}`; // switching FR/EN reloads the article in that language
  // Result tagged with its slug: moving to another article shows "loading" until it arrives
  const [result, setResult] = useState<{ slug: string; article: Article | null } | null>(null);
  const current = result?.slug === key ? result : null;
  const article = current?.article ?? null;
  const missing = !!current && !current.article;

  useEffect(() => {
    let cancelled = false;
    getArticle(slug).then(
      (a) => {
        if (cancelled) return;
        if ("redirect" in a) {
          // The article moved (Admin → Blog → Redirects): go to its new address
          if (/^https?:/i.test(a.redirect)) window.location.replace(a.redirect);
          else navigate(a.redirect, { replace: true });
          return;
        }
        setResult({ slug: key, article: a });
      },
      () => !cancelled && setResult({ slug: key, article: null }),
    );
    window.scrollTo({ top: 0 });
    return () => {
      cancelled = true;
    };
  }, [slug, key, navigate]);

  // Title through the blog's template, robots and canonical from the editor, article + FAQ structured data
  const url = absoluteUrl(`/blog/${slug}`);
  useSeo(
    missing
      ? { title: b.notFound, noindex: true }
      : !article
        ? null
        : {
            title: article.metaTitle || `${article.seoTitle} | Le Coin VTC`,
            fullTitle: true,
            description: article.seoDescription,
            canonical: article.canonicalUrl || url,
            noindex: article.noindex,
            nofollow: article.nofollow,
            type: "article",
            ogTitle: article.ogTitle,
            ogDescription: article.ogDescription,
            image: article.ogImage ? assetUrl(article.ogImage) : null,
            jsonLd: [
              {
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                headline: article.title,
                description: article.seoDescription,
                datePublished: article.publishedAt,
                dateModified: article.updatedAt,
                inLanguage: article.language,
                ...(article.author && { author: { "@type": "Person", name: article.author } }),
                ...(article.coverImage && { image: assetUrl(article.coverImage) }),
                ...(article.category && { articleSection: article.category.name }),
                ...(article.tags.length && { keywords: article.tags.join(", ") }),
                mainEntityOfPage: article.canonicalUrl || url,
                publisher: { "@id": ORGANIZATION_ID, "@type": "Organization", name: SITE_NAME, logo: absoluteUrl("/favicon.svg") },
              },
              ...(article.faq.length ? [faqLd(article.faq.map((f) => ({ q: f.question, a: f.answer })))] : []),
              breadcrumbLd([[b.home, "/"], [b.band, "/blog"], [article.title]]),
            ],
          },
  );
  const html = useMemo(() => (article ? renderMarkdown(article.content, assetUrl) : ""), [article]);

  const [y, m, d] = (article?.publishedAt ?? "").slice(0, 10).split("-").map(Number);
  const cta = article && article.cta !== "none" ? b.ctas[article.cta] : null;
  const initials = (article?.author ?? "")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/blog" />

      {/* Page band: section title and breadcrumb */}
      <div className="border-b border-line bg-surface">
        <div className="mx-auto w-full max-w-300 px-4 py-8 sm:px-6 sm:py-10">
          <div className="text-2xl font-semibold uppercase tracking-[0.3em] text-ink sm:text-[28px]">{b.band}</div>
          <nav aria-label="Breadcrumb" className="mt-2 text-xs text-muted">
            <span>{b.youAreHere} </span>
            <Link to="/" className="font-medium text-ink no-underline hover:underline">
              {b.home}
            </Link>
            <span className="mx-1.5 text-faint">/</span>
            <Link to="/blog" className="font-medium text-ink no-underline hover:underline">
              {b.band}
            </Link>
            {article && (
              <>
                <span className="mx-1.5 text-faint">/</span>
                <span aria-current="page">{article.title}</span>
              </>
            )}
          </nav>
        </div>
      </div>

      <main className="mx-auto grid w-full max-w-300 flex-1 gap-8 px-4 pt-8 pb-20 sm:px-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-10">
        <div className="min-w-0">
          {missing ? (
            <div className="rounded-lg bg-surface px-6 py-12 text-center">
              <h1 className="m-0 text-xl font-medium">{b.notFound}</h1>
              <Link to="/blog" className="mt-3 inline-block text-sm text-brand-link">
                {b.back}
              </Link>
            </div>
          ) : !article ? (
            <div className="h-96 animate-pulse rounded-lg bg-surface" aria-busy="true" />
          ) : (
            <article>
              <div className={`relative mb-4 overflow-hidden rounded-lg bg-[#E4E4E4] ${COVER_ASPECT}`}>
                {article.coverImage && (
                  <img src={assetUrl(article.coverImage)} alt={article.coverAlt || article.title} width={1200} height={630} className="h-full w-full object-cover" />
                )}
                <time
                  dateTime={article.publishedAt}
                  className="absolute top-3 left-3 flex w-12 flex-col items-center overflow-hidden rounded bg-white text-center shadow-sm sm:top-4 sm:left-4"
                >
                  <span className="w-full bg-ink py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{t.format.months[m - 1]}</span>
                  <span className="pt-1 text-xl leading-none font-semibold text-ink">{d}</span>
                  <span className="pb-1 text-[10px] text-muted">{y}</span>
                </time>
              </div>

              <div className="mb-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
                {article.author && (
                  <span>
                    {b.by} <span className="font-medium text-ink">{article.author}</span>
                  </span>
                )}
                {article.category && (
                  <span>
                    {b.in}{" "}
                    <Link to={`/blog?category=${article.category.slug}`} className="text-brand-link no-underline hover:underline">
                      {article.category.name}
                    </Link>
                  </span>
                )}
                <span>{b.minutes(article.readingMinutes)}</span>
                <span>{b.views(article.views)}</span>
              </div>

              <h1 className="m-0 text-[28px] font-semibold leading-tight tracking-[-0.01em] sm:text-[34px]">{article.title}</h1>
              {article.language !== lang && <p className="mt-4 mb-0 rounded bg-surface px-3 py-2 text-xs text-muted">{b.notTranslated}</p>}
              {article.excerpt && <p className="mt-4 mb-6 text-[16px] leading-relaxed text-ink-soft">{article.excerpt}</p>}
              <div className="mt-4 blog-prose text-[15px] leading-[1.75] text-ink" dangerouslySetInnerHTML={{ __html: html }} />

              {article.faq.length > 0 && (
                <section className="mt-10">
                  <h2 className="mt-0 mb-3 text-xl font-semibold">{b.faqTitle}</h2>
                  <div className="rounded-lg border border-line-strong">
                    {article.faq.map((f, i) => (
                      <details key={i} className="group border-b border-line-strong px-4 last:border-0" open={i === 0}>
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3.5 text-[15px] font-medium [&::-webkit-details-marker]:hidden">
                          {f.question}
                          <span className="text-lg leading-none text-faint transition-transform group-open:rotate-45" aria-hidden="true">
                            +
                          </span>
                        </summary>
                        <p className="mt-0 mb-4 text-sm leading-relaxed whitespace-pre-line text-ink-soft">{f.answer}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {cta && (
                <div className="mt-10 flex flex-col gap-4 rounded-lg bg-ink px-5 py-5 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div>
                    <div className="text-base font-semibold">{cta.title}</div>
                    <p className="mt-1 mb-0 text-[13px] text-white/70">{cta.text}</p>
                  </div>
                  <Link to={cta.href} className="inline-flex h-10 shrink-0 items-center justify-center rounded bg-brand px-5 text-[13px] font-medium text-white no-underline hover:bg-brand-dark">
                    {cta.button}
                  </Link>
                </div>
              )}

              {article.tags.length > 0 && (
                <div className="mt-6 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                  <span className="mr-1">{b.tags}:</span>
                  {article.tags.map((tag) => (
                    <Link key={tag} to={`/blog/tag/${article.tagSlugs?.[tag] ?? encodeURIComponent(tag.toLowerCase())}`} className="rounded bg-surface px-2 py-1 text-ink no-underline hover:bg-surface-hover">
                      {tag}
                    </Link>
                  ))}
                </div>
              )}

              {article.author && (
                <div className="mt-6 flex items-center gap-3 rounded-lg bg-surface p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-semibold text-white" aria-hidden="true">
                    {initials}
                  </span>
                  <div>
                    <div className="text-sm font-semibold">{article.author}</div>
                    <div className="text-xs text-muted">{article.authorBio || (article.authorRole && b.authorRoles[article.authorRole]) || "Le Coin VTC"}</div>
                  </div>
                </div>
              )}

              {article.related.length > 0 && (
                <section className="mt-12">
                  <h2 className="mt-0 mb-4 text-lg font-semibold">{b.related}</h2>
                  <div className="grid gap-4 sm:grid-cols-3">
                    {article.related.map((r) => (
                      <Link key={r.slug} to={`/blog/${r.slug}`} className="group text-ink no-underline">
                        <div className={`mb-2 overflow-hidden rounded-lg bg-[#E4E4E4] ${COVER_ASPECT}`}>
                          {r.coverImage && <img src={assetUrl(thumbOf(r.coverImage))} alt={r.coverAlt || r.title} loading="lazy" className="h-full w-full object-cover" />}
                        </div>
                        <div className="text-sm font-medium leading-snug group-hover:underline">{r.title}</div>
                        <div className="mt-0.5 text-[11px] text-faint">{longDate(r.publishedAt.slice(0, 10))}</div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          )}
        </div>

        <BlogSidebar />
      </main>
      <Footer />
    </div>
  );
}
